/**
 * AutoParkPlanningDialog - The "What-If" Planning Workspace
 *
 * A large dialog that transforms AutoPark from a "black box algorithm"
 * into a collaborative planning tool where operators can:
 * 1. See current state - All trips with their spot assignments visualized
 * 2. Lock what stays - Pin trips in place with visual clarity
 * 3. Preview changes - See exactly what WILL move before committing
 * 4. Compare before/after - Split view showing current vs proposed
 */

import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PreviewIcon from "@mui/icons-material/Preview";
import PrintIcon from "@mui/icons-material/Print";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addDays, differenceInDays, differenceInHours, differenceInMinutes } from "date-fns";
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ParkingConfigForm } from "../TemporalTest/components/ParkingConfigForm";
import { SpotOccupancyGantt } from "./components/SpotOccupancyGantt";
import { TripListPanel } from "./components/TripListPanel";
import { ImpactSummaryPanel } from "./components/ImpactSummaryPanel";
import { AutoParkPlanningDialogProps } from "./types";

export const AutoParkPlanningDialog: React.FC<AutoParkPlanningDialogProps> = ({
  open,
  onClose,
  timeRange,
  onTimeRangeChange,
  groupedTrips,
  // 3-state toggle model
  tripStates,
  onCycleTripState,
  selectedTripIds,
  lockedTripIds,
  // Batch actions
  onLockAll,
  onUnlockAll,
  onExcludeAll,
  onIncludeAll,
  // Lock suggestions
  lockSuggestions,
  onAcceptLockSuggestion,
  onAcceptAllSuggestions,
  // Preview
  previewMode,
  onPreviewModeChange,
  onRunPreview,
  isPreviewLoading,
  impactSummary,
  config,
  onConfigChange,
  onApplyResults,
  isApplying,
  ramp,
  placements,
  previewResult,
  onScheduleChanged,
  showDetailedView = false,
  availableLocations,
  onAssignToLocation,
}) => {
  const { t } = useTranslation();
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>("balanced");
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [hoveredTripId, setHoveredTripId] = useState<string | null>(null);
  const [hoveredConflictTimeRange, setHoveredConflictTimeRange] = useState<{ start: Date; end: Date } | null>(null);
  const [selectedConflictId, setSelectedConflictId] = useState<string | null>(null);
  const [selectedConflictTimeRange, setSelectedConflictTimeRange] = useState<{ start: Date; end: Date } | null>(null);

  // Map trip_id to aircraft_id for highlighting on Gantt
  const getAircraftIdForTrip = (tripId: string): string | null => {
    const requests = previewResult?.temporalData?.requests || [];
    const aircraft = requests.find((ac) => ac.trip_id === tripId);
    return aircraft?.id || null;
  };

  // Calculate placement percentage from statistics
  const placementPercent = useMemo(() => {
    if (!previewResult?.statistics) return 0;
    const stats = previewResult.statistics;
    if (stats.totalAircraft === 0) return 0;
    return Math.round((stats.placedAircraft / stats.totalAircraft) * 100);
  }, [previewResult?.statistics]);

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Algorithm presets for quick configuration
  const algorithmPresets = {
    balanced: {
      label: "Balanced (Default)",
      description: "Good balance of efficiency and stability",
      config: {
        bufferMinutes: 15,
        plannedMoveCap: 0,
        allowTrapping: true,
        egressWeight: 1,
        wasteWeight: 1,
        structuralWeight: 0.1,
        zonePriorityWeight: 1,
        min_dwell_weight: 2,
      },
    },
    conservative: {
      label: "Conservative",
      description: "Minimize moves, prioritize stability",
      config: {
        bufferMinutes: 30,
        plannedMoveCap: 0,
        allowTrapping: false,
        egressWeight: 0.5,
        wasteWeight: 0.5,
        structuralWeight: 0.2,
        zonePriorityWeight: 2,
        min_dwell_weight: 3,
      },
    },
    aggressive: {
      label: "Aggressive",
      description: "Maximize space efficiency",
      config: {
        bufferMinutes: 10,
        plannedMoveCap: 2,
        allowTrapping: true,
        egressWeight: 2,
        wasteWeight: 2,
        structuralWeight: 0.05,
        zonePriorityWeight: 0.5,
        min_dwell_weight: 1,
      },
    },
    custom: {
      label: "Custom",
      description: "Manual configuration",
      config: null,
    },
  };

  const handlePresetChange = (presetKey: string) => {
    setSelectedPreset(presetKey);
    const preset = algorithmPresets[presetKey as keyof typeof algorithmPresets];
    if (preset.config) {
      onConfigChange(preset.config);
    }
  };

  // Calculate duration between start and end times
  const calculateDuration = (start: Date, end: Date) => {
    const days = differenceInDays(end, start);
    const hours = differenceInHours(end, start) % 24;
    const minutes = differenceInMinutes(end, start) % 60;

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Quick duration preset handlers
  const handleOneDayRange = () => {
    const newEnd = addDays(timeRange.start, 1);
    onTimeRangeChange(timeRange.start, newEnd);
  };

  const handleThreeDayRange = () => {
    const newEnd = addDays(timeRange.start, 3);
    onTimeRangeChange(timeRange.start, newEnd);
  };

  const handleSevenDayRange = () => {
    const newEnd = addDays(timeRange.start, 7);
    onTimeRangeChange(timeRange.start, newEnd);
  };

  // Check if we have unlocked trips (for preview button)
  // Trips that are 'unlocked' (not locked and not excluded) can be moved by the algorithm
  const hasUnlockedTrips = groupedTrips.some((trip) => {
    const state = tripStates.get(trip.trip_id) || 'unlocked';
    return state === 'unlocked';
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "100vw",
          maxWidth: "100vw",
          height: "100vh",
          maxHeight: "100vh",
          margin: 0,
          borderRadius: 0,
        },
      }}
    >
      <DialogTitle sx={{ px: 2, py: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
              {t("screens.ramp.autoPark.planningDialog.title")}
            </Typography>
            <Divider orientation="vertical" flexItem />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DateTimePicker
                  value={timeRange.start}
                  onChange={(newValue) => {
                    if (newValue) onTimeRangeChange(newValue, timeRange.end);
                  }}
                  ampm={false}
                  slotProps={{
                    textField: { size: "small", sx: { width: 210 }, placeholder: "Start" },
                  }}
                />
                <Typography variant="body2" color="text.secondary">→</Typography>
                <DateTimePicker
                  value={timeRange.end}
                  onChange={(newValue) => {
                    if (newValue) onTimeRangeChange(timeRange.start, newValue);
                  }}
                  ampm={false}
                  slotProps={{
                    textField: { size: "small", sx: { width: 210 } },
                  }}
                />
                <ButtonGroup size="small" variant="outlined">
                  <Button onClick={handleOneDayRange}>1d</Button>
                  <Button onClick={handleThreeDayRange}>3d</Button>
                  <Button onClick={handleSevenDayRange}>7d</Button>
                </ButtonGroup>
                <Typography variant="caption" color="text.secondary">
                  ({calculateDuration(timeRange.start, timeRange.end)})
                </Typography>
              </Stack>
            </LocalizationProvider>
            {/* Stats (when preview results exist) */}
            {previewResult?.statistics && (
              <>
                <Divider orientation="vertical" flexItem />
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2">
                    {t("screens.ramp.autoPark.planningDialog.stats.placed", {
                      placed: previewResult.statistics.placedAircraft || 0,
                      total: previewResult.statistics.totalAircraft || 0,
                    })}
                  </Typography>
                  <Typography variant="body2">
                    {t("screens.ramp.autoPark.planningDialog.stats.rate", {
                      percent: placementPercent,
                    })}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
          {/* Action buttons */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            {previewResult && (
              <IconButton size="small" onClick={handlePrint} title={t("screens.ramp.autoPark.planningDialog.buttons.print")}>
                <PrintIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={(e) => setSettingsAnchor(e.currentTarget)} title={t("screens.ramp.autoPark.planningDialog.buttons.settings")}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {/* Main Content Area - Two Panels */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            borderTop: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {/* Left Panel - Trip List with Lock Controls */}
          {!isLeftPanelCollapsed && (
            <Box
              sx={{
                width: 360,
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <TripListPanel
                groupedTrips={groupedTrips}
                tripStates={tripStates}
                onCycleTripState={onCycleTripState}
                onLockAll={onLockAll}
                onUnlockAll={onUnlockAll}
                onExcludeAll={onExcludeAll}
                onIncludeAll={onIncludeAll}
                impactSummary={impactSummary}
                previewMode={previewResult ? "preview" : "current"}
                onTripHover={setHoveredTripId}
                showDetailedView={showDetailedView}
                availableLocations={availableLocations}
                onAssignToLocation={onAssignToLocation}
              />
            </Box>
          )}

          {/* Collapse/Expand Toggle */}
          <Box
            sx={{
              width: 24,
              bgcolor: "grey.100",
              borderLeft: 1,
              borderRight: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": {
                bgcolor: "grey.200",
              },
            }}
            onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
          >
            <Tooltip title={isLeftPanelCollapsed
              ? t("screens.ramp.autoPark.planningDialog.tooltips.showTripList")
              : t("screens.ramp.autoPark.planningDialog.tooltips.hideTripList")}>
              <IconButton size="small" sx={{ p: 0 }}>
                {isLeftPanelCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Right Panel - Timeline/Gantt View */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            {/* Timeline Visualization */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              {previewResult ? (
                // Preview View - Show proposed placements
                <Box sx={{ height: "100%", overflow: "auto", p: 1 }}>
                  <SpotOccupancyGantt
                    spots={previewResult.temporalData?.spots || []}
                    aircraft={previewResult.temporalData?.requests || []}
                    placement={previewResult.placement}
                    horizonHours={previewResult.temporalData?.horizonHours || 24}
                    startTime={previewResult.temporalData?.startTime || timeRange.start}
                    zones={previewResult.temporalData?.zones}
                    rampShapes={ramp.shapes}
                    hideStatistics
                    enableEditing={true}
                    onScheduleChanged={onScheduleChanged}
                    highlightedAircraftId={hoveredTripId ? getAircraftIdForTrip(hoveredTripId) : null}
                    highlightedTimeRange={selectedConflictTimeRange || hoveredConflictTimeRange}
                  />
                </Box>
              ) : (
                // Empty State - Show guided workflow
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                    color: "text.secondary",
                    p: 4,
                  }}
                >
                  <PreviewIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                  <Typography variant="h6" fontWeight={500}>
                    {t("screens.ramp.autoPark.planningDialog.workflow.title")}
                  </Typography>
                  <Box
                    component="ol"
                    sx={{
                      m: 0,
                      pl: 2.5,
                      "& li": { mb: 1 },
                      maxWidth: 400,
                    }}
                  >
                    <li>
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                          __html: t("screens.ramp.autoPark.planningDialog.workflow.step1"),
                        }}
                      />
                    </li>
                    <li>
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                          __html: t("screens.ramp.autoPark.planningDialog.workflow.step2"),
                        }}
                      />
                    </li>
                    <li>
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                          __html: t("screens.ramp.autoPark.planningDialog.workflow.step3"),
                        }}
                      />
                    </li>
                    <li>
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                          __html: t("screens.ramp.autoPark.planningDialog.workflow.step4"),
                        }}
                      />
                    </li>
                  </Box>
                  <Divider sx={{ width: "100%", maxWidth: 400, my: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 400, textAlign: "center" }}>
                    {t("screens.ramp.autoPark.planningDialog.workflow.footer")}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Impact Summary (when preview available) */}
        {impactSummary && previewResult && (
          <ImpactSummaryPanel
            impactSummary={impactSummary}
            onMoveHover={setHoveredTripId}
            onConflictHover={(conflict) => {
              // Only update hover if nothing is selected (selected takes priority)
              if (!selectedConflictId) {
                if (conflict) {
                  setHoveredConflictTimeRange({
                    start: new Date(conflict.startTime),
                    end: new Date(conflict.endTime),
                  });
                } else {
                  setHoveredConflictTimeRange(null);
                }
              }
            }}
            onConflictClick={(conflict) => {
              if (conflict) {
                setSelectedConflictId(conflict.tripId);
                setSelectedConflictTimeRange({
                  start: new Date(conflict.startTime),
                  end: new Date(conflict.endTime),
                });
              } else {
                setSelectedConflictId(null);
                setSelectedConflictTimeRange(null);
              }
            }}
            selectedConflictId={selectedConflictId}
          />
        )}

        {/* Algorithm Settings Popover */}
        <Popover
          open={Boolean(settingsAnchor)}
          anchorEl={settingsAnchor}
          onClose={() => setSettingsAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ p: 3, width: 420, maxHeight: "70vh", overflow: "auto" }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{t("screens.ramp.autoPark.planningDialog.algorithmSettings")}</Typography>
              <Tooltip title={t("screens.ramp.autoPark.planningDialog.tooltips.configureAlgorithm")}>
                <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: "help" }} />
              </Tooltip>
            </Stack>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t("screens.ramp.autoPark.planningDialog.presets.label")}</InputLabel>
              <Select
                value={selectedPreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                label={t("screens.ramp.autoPark.planningDialog.presets.label")}
                renderValue={(value) =>
                  t(`screens.ramp.autoPark.planningDialog.presets.${value}.label`)
                }
              >
                {Object.keys(algorithmPresets).map((key) => (
                  <MenuItem key={key} value={key}>
                    <Stack>
                      <Typography variant="body2" fontWeight={500}>
                        {t(`screens.ramp.autoPark.planningDialog.presets.${key}.label`)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(`screens.ramp.autoPark.planningDialog.presets.${key}.description`)}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider sx={{ my: 2 }} />
            <ParkingConfigForm
              bufferMinutes={config.bufferMinutes}
              plannedMoveCap={config.plannedMoveCap}
              allowTrapping={config.allowTrapping}
              egressWeight={config.egressWeight}
              wasteWeight={config.wasteWeight}
              structuralWeight={config.structuralWeight}
              zonePriorityWeight={config.zonePriorityWeight}
              min_dwell_weight={config.min_dwell_weight}
              sortingStrategy={config.sortingStrategy}
              arrivalStagingDwellMinutes={config.arrivalStagingDwellMinutes}
              departureStagingDwellMinutes={config.departureStagingDwellMinutes}
              quickTurnThresholdMinutes={config.quickTurnThresholdMinutes}
              onBufferMinutesChange={(value) =>
                onConfigChange({ bufferMinutes: value })
              }
              onPlannedMoveCapChange={(value) =>
                onConfigChange({ plannedMoveCap: value })
              }
              onAllowTrappingChange={(value) =>
                onConfigChange({ allowTrapping: value })
              }
              onEgressWeightChange={(value) =>
                onConfigChange({ egressWeight: value })
              }
              onWasteWeightChange={(value) =>
                onConfigChange({ wasteWeight: value })
              }
              onStructuralWeightChange={(value) =>
                onConfigChange({ structuralWeight: value })
              }
              onZonePriorityWeightChange={(value) =>
                onConfigChange({ zonePriorityWeight: value })
              }
              onMinDwellWeightChange={(value) =>
                onConfigChange({ min_dwell_weight: value })
              }
              onSortingStrategyChange={(value) =>
                onConfigChange({ sortingStrategy: value })
              }
              onArrivalStagingDwellMinutesChange={(value) =>
                onConfigChange({ arrivalStagingDwellMinutes: value })
              }
              onDepartureStagingDwellMinutesChange={(value) =>
                onConfigChange({ departureStagingDwellMinutes: value })
              }
              onQuickTurnThresholdMinutesChange={(value) =>
                onConfigChange({ quickTurnThresholdMinutes: value })
              }
              size="small"
              layout="column"
              disabledFields={[]}
              disabledSortingOptions={[
                "arrival",
                "longest-dwell",
                "latest-departure",
                "hybrid",
              ]}
            />
          </Box>
        </Popover>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "flex-end" }}>
        <Button onClick={onClose}>{t("screens.ramp.autoPark.planningDialog.buttons.cancel")}</Button>
        <Tooltip
          title={
            !hasUnlockedTrips
              ? groupedTrips.length === 0
                ? t("screens.ramp.autoPark.planningDialog.tooltips.noTripsInRange")
                : t("screens.ramp.autoPark.planningDialog.tooltips.unlockToPreview")
              : ""
          }
        >
          <span>
            <Button
              variant="outlined"
              startIcon={isPreviewLoading ? <CircularProgress size={16} /> : <PreviewIcon />}
              onClick={onRunPreview}
              disabled={isPreviewLoading || !hasUnlockedTrips}
            >
              {isPreviewLoading
                ? t("screens.ramp.autoPark.planningDialog.buttons.running")
                : t("screens.ramp.autoPark.planningDialog.buttons.preview")}
            </Button>
          </span>
        </Tooltip>
        {previewResult && onApplyResults && (
          <Button
            onClick={onApplyResults}
            variant="contained"
            color="primary"
            startIcon={isApplying ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            disabled={isApplying}
          >
            {isApplying
              ? t("screens.ramp.autoPark.planningDialog.buttons.applying")
              : t("screens.ramp.autoPark.planningDialog.buttons.applyToRamp")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
